<template lang="pug">
.challenge-leaderboard
  .leaderboard-header
    .left
      h1 {{ __(`${getLeaderboardTitle} Leaderboard`) }}
    .right
      RogueButton.outline.black-outline(@click="refreshResults()") {{ __('Refresh Results') }}

  //- App-specific actions row with both buttons side-by-side
  .app-actions-row(v-if="isAppContext")
    RogueButton.app-btn(:key="appShowFilters" color="gray" width="full" @click="toggleFilters") {{ filterButtonText }}
    RogueButton.app-btn(color="gray" width="full" @click="refreshResults()") {{ __('Refresh Results') }}

  //- App-specific filters section
  .app-filters-section(v-if="isAppContext && appShowFilters")
    .filter-row
      RogueSelect.filter-select(
        :options="appCompetitions.length ? appCompetitions : [{ label: 'No options', value: '' }]"
        label="Competition"
        v-model="appSelectedValues.competition"
        @update:modelValue="appUpdateFilters"
      )
      RogueSelect.filter-select(
        :options="appSeasons.length ? appSeasons : [{ label: 'No options', value: '' }]"
        label="Season"
        v-model="appSelectedValues.season"
        @update:modelValue="appUpdateFilters"
      )
    .filter-row
      RogueSelect.filter-select(
        :options="appSeries.length ? appSeries : [{ label: 'No options', value: '' }]"
        label="Challenge"
        v-model="appSelectedValues.series"
        @update:modelValue="appUpdateFilters"
      )
      RogueSelect.filter-select(
        :options="appGenderOptions.length ? appGenderOptions : [{ label: 'No options', value: '' }]"
        label="Gender"
        v-model="appSelectedValues.gender"
        @update:modelValue="appUpdateFilters"
      )
    .filter-row(v-if="appScaleOptions.length > 1 || appWeightOptions.length > 1")
      RogueSelect.filter-select(
        v-if="appScaleOptions.length > 1"
        :options="appScaleOptions"
        label="Scale"
        v-model="appSelectedValues.scale"
        @update:modelValue="appUpdateFilters"
      )
      RogueSelect.filter-select(
        v-if="appWeightOptions.length > 1"
        :options="appWeightOptions"
        label="Weight"
        v-model="appSelectedValues.weight"
        @update:modelValue="appUpdateFilters"
      )

  //- Web filters (hidden in app context via CSS)
  LeaderboardFilters.web-filters(
    v-if="events.length > 0"
    @loadLeaderboard="loadLeaderboard($event)"
    :events="events"
    :selectedDivision="selectedDivision"
    :selectedEvent="selectedEvent"
    :singleLeaderboard="singleLeaderboard"
    :defaultEventSeason="defaultEventSeason"
  )
  .search-pagination-row
    .search
      RogueInput(
        type="text"
        placeholder="Search by Name or Gym"
        v-model="name"
        @update:modelValue="handleFilterChange"
      )
    .pagination
      .pagination-left(@click="updatePage(page - 1)")
        Icon(name="arrow-left" :fill="page === 1 ? '#A2A2A2' : '#FFF'")
      p Page {{ page }}
      .pagination-right(@click="updatePage(page + 1)")
        Icon(name="arrow-right" :fill="results.length < PAGE_SIZE ? '#A2A2A2' : '#FFF'")
  loading(v-if="isLoading")
  OverallResults(
    v-else-if="selectedEvent?.overallLeaderboard && selectedDivision"
    @updateSort="updateSort($event)"
    :selectedDivision="selectedDivision"
    :selectedEvent="selectedEvent"
    :name="name"
    :events="events"
    :results="results"
    :sortChallengeNumber="sortChallengeNumber"
    :sortMeasurementKey="sortMeasurementKey"
    :sortOrder="sortOrder"
    :showResults="showResults"
  )
  CompetitorResults(
    v-else-if="selectedEvent && selectedDivision"
    @updateSort="updateSort($event)"
    :selectedDivision="selectedDivision"
    :selectedEvent="selectedEvent"
    :name="name"
    :events="events"
    :results="results"
    :sortMeasurementKey="sortMeasurementKey"
    :sortOrder="sortOrder"
    :showResults="showResults"
  )
  .no-results(v-else-if="!isLoading")
    p {{ __('No results') }}
</template>
<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import type { IEvent, IDivision } from './leaderboard-types'
import LeaderboardFilters from './leaderboard-filters.vue'
import CompetitorResults from './competitor-results.vue'
import OverallResults from './overall-results.vue'
import debounce from '@/js/helpers/debounce'
import { useRogue } from '@/js/services/Rogue'

const PAGE_SIZE = 20

const rogue = useRogue()
const route = useRoute()

const props = withDefaults(defineProps<{
  leaderboardTitle?: string | null,
  defaultDivision?: string | null,
  defaultEventSeason?: string,
  defaultEventSku?: string | null,
  singleLeaderboard?: boolean,
}>(), {
  leaderboardTitle   : null,
  defaultDivision    : null,
  defaultEventSeason : '2026',
  defaultEventSku    : null,
  singleLeaderboard  : false,
})

const events = ref<IEvent[]>([])
const results = ref<any[]>([])
const selectedEvent = ref<IEvent | null>(null)
const selectedDivision = ref<IDivision | null>(null)
const country = ref<string | undefined>('worldwide')
const isLoading = ref(true)
const page = ref(1)
const name = ref('')

// App-specific state
const isAppContext = computed(() => !!rogue?.appContext)
const appShowFilters = ref(false)

function toggleFilters() {
  appShowFilters.value = !appShowFilters.value
}

const filterButtonText = computed(() => appShowFilters.value ? rogue.__('Hide Filters') : rogue.__('Show Filters'))

const appSelectedValues = reactive({
  competition : '',
  season      : '',
  series      : '',
  gender      : '',
  scale       : '',
  weight      : '',
})

// App-specific filter options
const appCompetitions = computed(() => {
  const types = events.value
    .filter(e => e.eventType !== 'Record Breakers' && e.eventType !== 'CrossFit')
    .map(e => e.eventType)
  const unique = [...new Set(types)]
  
  return unique.map(t => ({ label: t, value: t }))
})

const appSeasons = computed(() => {
  const years = events.value
    .filter(e => e.eventType === appSelectedValues.competition)
    .map(e => e.season || '')
    .filter(Boolean)
  const unique = [...new Set(years)].sort((a, b) => Number(b) - Number(a))
  
  return unique.map(y => ({ label: y, value: y }))
})

const appSeries = computed(() => {
  return events.value
    .filter(e => e.eventType === appSelectedValues.competition && e.season === appSelectedValues.season)
    .map(e => ({ label: e.name, value: e.name }))
})

const appCurrentDivisions = computed(() => {
  return selectedEvent.value?.divisions || []
})

const appGenderOptions = computed(() => {
  const genders = appCurrentDivisions.value
    .map(d => d.attributes?.gender)
    .filter(Boolean)
  const unique = [...new Set(genders)]
  
  return unique.map(g => ({ label: g, value: g }))
})

const appScaleOptions = computed(() => {
  let divisions = appCurrentDivisions.value
  if (appSelectedValues.gender) {
    divisions = divisions.filter(d => d.attributes?.gender === appSelectedValues.gender)
  }
  const scales = divisions
    .map(d => d.attributes?.scale)
    .filter(Boolean)
  const unique = [...new Set(scales)]

  return unique.map(s => ({ label: s, value: s }))
})

const appWeightOptions = computed(() => {
  let divisions = appCurrentDivisions.value
  if (appSelectedValues.gender) {
    divisions = divisions.filter(d => d.attributes?.gender === appSelectedValues.gender)
  }
  if (appSelectedValues.scale) {
    divisions = divisions.filter(d => d.attributes?.scale === appSelectedValues.scale)
  }
  const weights = divisions
    .map(d => d.attributes?.weight)
    .filter(Boolean)
  const unique = [...new Set(weights)].sort((a, b) => Number(a) - Number(b))

  return unique.map(w => ({ label: w, value: w }))
})


function appUpdateFilters() {
  // Find matching event
  const newEvent = events.value.find(e =>
    e.eventType === appSelectedValues.competition &&
    e.season === appSelectedValues.season &&
    e.name === appSelectedValues.series
  ) || null

  // If event changed, we need to validate/reset filter values
  const eventChanged = selectedEvent.value?.sku !== newEvent?.sku
  selectedEvent.value = newEvent

  if (selectedEvent.value) {
    // Get available divisions for this event
    const availableDivisions = selectedEvent.value.divisions || []

    // Get available genders for this event
    const availableGenders = [...new Set(availableDivisions.map(d => d.attributes?.gender).filter(Boolean))]

    // Reset gender if not available in new event, or set default if empty
    if (eventChanged) {
      if (!availableGenders.includes(appSelectedValues.gender)) {
        appSelectedValues.gender = availableGenders[0] || ''
      }
    }

    // Get available scales for current gender
    const divisionsForGender = appSelectedValues.gender
      ? availableDivisions.filter(d => d.attributes?.gender === appSelectedValues.gender)
      : availableDivisions
    const availableScales = [...new Set(divisionsForGender.map(d => d.attributes?.scale).filter(Boolean))]

    // Reset scale if not available, or set default if there are options
    if (!availableScales.includes(appSelectedValues.scale)) {
      appSelectedValues.scale = availableScales.length > 0 ? availableScales[0] : ''
    }

    // Get available weights for current gender + scale
    const divisionsForScale = appSelectedValues.scale
      ? divisionsForGender.filter(d => d.attributes?.scale === appSelectedValues.scale)
      : divisionsForGender
    const availableWeights = [...new Set(divisionsForScale.map(d => d.attributes?.weight).filter(Boolean))]

    // Reset weight if not available, or set default if there are options
    if (!availableWeights.includes(appSelectedValues.weight)) {
      appSelectedValues.weight = availableWeights.length > 0 ? availableWeights[0] : ''
    }

    // Find matching division with validated filter values
    selectedDivision.value = availableDivisions.find(d => {
      const attrs = d.attributes || {}
      const genderMatch = !appSelectedValues.gender || attrs.gender === appSelectedValues.gender
      const scaleMatch = !appSelectedValues.scale || attrs.scale === appSelectedValues.scale
      const weightMatch = !appSelectedValues.weight || attrs.weight === appSelectedValues.weight

      return genderMatch && scaleMatch && weightMatch
    }) || availableDivisions[0] || null
  } else {
    selectedDivision.value = null
  }

  page.value = 1
  loadResults()
}
const sortOrder = ref('asc')
const sortMeasurementKey = ref('')
const sortChallengeNumber = ref<number>()
const sortType = ref('event_rank')
const showResults = ref(false)

async function loadResults() {
  isLoading.value = true

  if (selectedDivision.value && selectedEvent.value) {
    let url = selectedEvent.value.overallLeaderboard
      ? `/svc_event/events/overall/leaderboard/${selectedEvent.value.season}/${selectedDivision.value.attributes.gender.toLowerCase()}?page=${page.value}`
      : `/svc_event/events/${selectedEvent.value.season}/${selectedEvent.value.sku}/divisions/${selectedDivision.value.id}/leaderboard?page=${page.value}`

    if (name.value) {
      url += `&name=${encodeURIComponent(name.value)}`
    }

    if (!selectedEvent.value.overallLeaderboard) {
      url += `&sortEventNumber=1`
    }

    if (selectedEvent.value.name === 'Bandit Challenge') {
      url += '&sortType=over_rank'
    }

    if (sortType.value && !selectedEvent.value.overallLeaderboard && selectedEvent.value.eventType === 'Challenge') {
      url += `&sortType=${sortType.value}`
    } else {
      url += '&sortType=over_rank'
    }

    if (country.value && country.value !== 'worldwide') {
      url += `&nationality=${country.value}`
    }

    if (sortMeasurementKey.value) {
      url += `&sortMeasurementKey=${sortMeasurementKey.value}`
    }

    if (sortChallengeNumber.value) {
      url += `&sortChallengeNumber=${sortChallengeNumber.value}`
    }

    if (sortOrder.value) {
      url += `&sortOrder=${sortOrder.value}`
    }

    try {
      results.value = await rogue.api.fetch<any[]>({ url })
      showResults.value = true
    } catch (e) {
      showResults.value = false
    }
  } else {
    results.value = []
  }
  isLoading.value = false
}

async function refreshResults() {
  name.value = ''
  sortMeasurementKey.value = 'rank'
  sortOrder.value = 'asc'
  page.value = 1
  await loadResults()
}

async function loadLeaderboard(e: {newEvent: IEvent, selectedDivision?: IDivision, country?: string}) {
  if (!e || !e.newEvent) {
    return
  }
  // An event with no divisions configured still switches the leaderboard - it just has no results
  selectedEvent.value = e.newEvent
  selectedDivision.value = e.selectedDivision || null
  country.value = e.country
  page.value = 1
  await loadResults()
}

async function updateSort(e: { sortMeasurementKey: string, sortChallengeNumber: undefined | number, sortOrder: string }) {
  if (!e || (!e.sortMeasurementKey && !e.sortChallengeNumber)) {
    return
  }
  sortOrder.value = e.sortOrder
  sortMeasurementKey.value = e.sortMeasurementKey
  sortChallengeNumber.value = e.sortChallengeNumber

  await loadResults()
}

async function updatePage(newPage: number) {
  if (newPage < 1 || (newPage > page.value && results.value.length < PAGE_SIZE)) {
    return
  }

  page.value = newPage
  await loadResults()
}

const handleFilterChange = debounce(() => {
  page.value = 1
  loadResults()
}, 300)

onMounted(async () => {
  events.value = await rogue.api.fetch<IEvent[]>({ url: '/svc_event/events' })

  const overrideSelectedDivision = (route.query?.sid as string | undefined)?.toUpperCase()

  if (overrideSelectedDivision) {
    // url param to specify which leaderboard to load from a link on the site
    const matches = events.value
      .map(event => ({ event, division: event.divisions?.find(d => d.id.toUpperCase() === overrideSelectedDivision) }))
      .filter(m => !!m.division)
    // If mulitple divisions match this SID, take the one in the current seaeson or the latest one
    const match = matches.find(m => m.event.season === props.defaultEventSeason) || matches.at(-1)
    selectedEvent.value = match?.event || null
    selectedDivision.value = match?.division || null
  } else {
    // use props to determine event and division to load
    selectedEvent.value = events.value.find(event => event.sku === props.defaultEventSku && event.season === props.defaultEventSeason)
      || events.value.findLast(e => e.season === props.defaultEventSeason && e.eventType === 'Challenge') || null
    selectedDivision.value = selectedEvent.value?.divisions?.find(division => division.id === props.defaultDivision) || selectedEvent.value?.divisions?.[0] || null
  }

  // Initialize app filter values if in app context
  if (isAppContext.value && selectedEvent.value) {
    appSelectedValues.competition = selectedEvent.value.eventType
    appSelectedValues.season = selectedEvent.value.season || ''
    appSelectedValues.series = selectedEvent.value.name
    if (selectedDivision.value?.attributes?.gender) {
      appSelectedValues.gender = selectedDivision.value.attributes.gender
    }
    // Set default scale/weight if available
    if (selectedDivision.value?.attributes?.scale) {
      appSelectedValues.scale = selectedDivision.value.attributes.scale
    }
    if (selectedDivision.value?.attributes?.weight) {
      appSelectedValues.weight = selectedDivision.value.attributes.weight
    }
  }

  await loadResults()
})

const getLeaderboardTitle = computed(() => selectedEvent.value?.name || 'Challenge')
</script>

<style lang="scss" scoped>
.challenge-leaderboard {
  display: block;
  max-width: 1776px;
  margin-top: $space-l;
  margin-bottom: $space-l;

  h1 {
    margin: 0;
  }
}

.leaderboard-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $size-s;
}

.search-pagination-row {
  background: $color-black;
  color: $color-white;
  display: flex;
  justify-content: space-between;
  padding: $size-s;
}

.pagination {
  display: flex;
  align-items: center;
  user-select: none;

  p {
    margin: 0 $space-s;
    text-transform: uppercase;
    white-space: nowrap;
  }
}

.search label {
  margin: 0;
}

.no-results {
  text-align: center;
  padding: $space-xl;
  color: $color-gray-dark;
  font-size: $size-s;
}

// Hide app-specific elements in web context by default
.app-actions-row,
.app-filters-section {
  display: none;
}

</style>

<style lang="scss" scoped target="app">
// Mobile app-specific styling for native webview
.platform-ios, .platform-android {
  .challenge-leaderboard {
    margin-top: 0;
    margin-bottom: 0;
    padding: $space-xs $space-s $space-s;
    min-height: 100vh;
    background: $color-white;
  }

  .leaderboard-header {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: $space-xxs;
    margin-left: -$space-s;
    margin-right: -$space-s;

    .left {
      width: 100%;
      margin-bottom: $space-xxs;

      h1 {
        font-family: $font-condensed;
        font-size: $size-l;
        font-weight: bold;
        text-transform: uppercase;
        color: $color-black;
        text-align: left;
        line-height: 1.2;
      }
    }

    // Hide original refresh button - we show it in app-actions-row instead
    .right {
      display: none !important;
    }
  }

  // Hide web filters in app context
  .web-filters {
    display: none !important;
  }

  // App-specific actions row with both buttons - extend to match search bar width
  .app-actions-row {
    display: flex !important;
    gap: $space-xs;
    margin-bottom: 0;
    margin-left: -$space-s;
    margin-right: -$space-s;

    // .app-btn is applied directly to the <button> element (RogueButton renders as button)
    .app-btn {
      flex: 1 1 0 !important;
      min-width: 0 !important;
      width: 0 !important; // Force equal flex basis
      height: 50px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: linear-gradient(180deg, #f2f2f2 0%, #e0e0e0 50%, #d6d6d6 100%) !important;
      border: 1px solid #9e9e9e !important;
      color: $color-black !important;
      font-family: $font-condensed;
      font-size: $size-s !important;
      font-weight: bold;
      text-transform: uppercase;
      padding: $space-s !important;
      border-radius: 6px !important;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 1px 1px rgba(0, 0, 0, 0.06) !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // App-specific filters section
  .app-filters-section {
    display: block;
    background: $color-gray-x-light;
    padding: $space-s 0;
    margin-bottom: 0;
    margin-left: -$space-s;
    margin-right: -$space-s;
    border-radius: 0;

    .filter-row {
      display: flex;
      gap: $space-xs;
      margin-bottom: $space-xs;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .filter-select {
      flex: 1;
      min-width: 0;
      --select-wrap-margin: 0;
      --select-font-size: #{$size-xxs};
      --select-padding: #{$space-xxs};
      --field-label-font-size: #{$size-xxs};
    }
  }

  .search-pagination-row {
    margin-left: -$space-s;
    margin-right: -$space-s;
    padding: $space-s;
    background: $color-black;

    .search {
      flex: 1;
      min-width: 0;
      --input-font-size: #{$size-xs};
      --input-padding: #{$space-xs} #{$space-s};
      --input-background: #{$color-white};
      --input-border: 1px solid #{$color-gray-light};
      --input-border-radius: 4px;
      --input-color: #{$color-black};
      --input-width: 100%;
      --field-margin: 0;
    }

    .pagination {
      flex-shrink: 0;
      color: $color-white;

      p {
        color: $color-white;
        font-size: $size-xs;
        font-weight: bold;
      }

      .pagination-left,
      .pagination-right {
        cursor: pointer;
        padding: $space-xxs;
      }
    }
  }
}
</style>
